package at.theatervogelweide.admin.web.screens.imprint

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import at.theatervogelweide.admin.contract.AdminError
import at.theatervogelweide.admin.contract.AdminResult
import at.theatervogelweide.admin.contract.ErrorCode
import at.theatervogelweide.admin.contract.FieldError
import at.theatervogelweide.admin.contract.club.Club
import at.theatervogelweide.admin.contract.imprint.Imprint
import at.theatervogelweide.admin.contract.imprint.ImprintDraft
import at.theatervogelweide.admin.contract.imprint.ImprintService
import at.theatervogelweide.admin.web.Route
import at.theatervogelweide.admin.web.components.AdminButton
import at.theatervogelweide.admin.web.components.AdminCallout
import at.theatervogelweide.admin.web.components.AdminCard
import at.theatervogelweide.admin.web.components.AdminConflictCallout
import at.theatervogelweide.admin.web.components.AdminMarkdownField
import at.theatervogelweide.admin.web.components.AdminMarkdownView
import at.theatervogelweide.admin.web.components.AdminPageBody
import at.theatervogelweide.admin.web.components.AdminPageHeader
import at.theatervogelweide.admin.web.components.AdminTextField
import at.theatervogelweide.admin.web.components.AdminTextView
import at.theatervogelweide.admin.web.components.Bem
import at.theatervogelweide.admin.web.components.ButtonVariant
import at.theatervogelweide.admin.web.components.PublicSiteLink
import at.theatervogelweide.admin.web.components.ReportUnsavedChanges
import at.theatervogelweide.admin.web.components.RouteLink
import at.theatervogelweide.admin.web.components.UnsavedChangesGuard
import at.theatervogelweide.admin.web.rpcCall
import at.theatervogelweide.admin.web.screens.acts.Strong
import at.theatervogelweide.admin.web.screens.acts.formatDate
import at.theatervogelweide.domain.Bic
import at.theatervogelweide.domain.Iban
import kotlinx.coroutines.launch
import kotlinx.rpc.RpcClient
import kotlinx.rpc.withService
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private val alert = Bem("admin-alert")
private val button = Bem("admin-button")
private val card = Bem("admin-card")
private val field = Bem("admin-field")
private val fieldset = Bem("admin-fieldset")
private val formGrid = Bem("admin-form-grid")
private val stack = Bem("admin-stack")

/** The Impressum as loaded, and the fields being edited; the IBAN in groups of four, as the form shows it. */
private class ImprintEditorState(val loaded: Imprint) {
    var draft: ImprintDraft by mutableStateOf(loaded.draft.copy(iban = Iban.grouped(loaded.draft.iban)))
    var fieldErrors: List<FieldError> by mutableStateOf(emptyList())
    var error: String? by mutableStateOf(null)
    var conflict: Boolean by mutableStateOf(false)
    var saving: Boolean by mutableStateOf(false)
    var reloading: Boolean by mutableStateOf(false)

    val busy: Boolean get() = saving || reloading

    // as stored: an IBAN only regrouped, or a space typed after a street, is no change
    val hasChanges: Boolean get() = draft.normalized() != loaded.draft.normalized()

    fun errorOf(name: String): String? = fieldErrors.firstOrNull { it.field == name }?.message

    /** Changes the draft; the field's error goes once it is edited. */
    fun edit(name: String, change: ImprintDraft.() -> ImprintDraft) {
        draft = draft.change()
        fieldErrors = fieldErrors.filterNot { it.field == name }
    }

    /** On leaving the field: the IBAN in capitals and groups of four, the BIC in capitals, each checked at once. */
    fun tidy(name: String) {
        draft = when (name) {
            ImprintDraft.IBAN -> draft.copy(iban = Iban.grouped(draft.iban))
            ImprintDraft.BIC -> draft.copy(bic = Bic.normalize(draft.bic))
            else -> draft
        }
        fieldErrors = fieldErrors.filterNot { it.field == name } + draft.validate().filter { it.field == name }
    }

    fun showFailure(failure: AdminError) {
        conflict = failure.code == ErrorCode.CONFLICT
        fieldErrors = failure.fieldErrors
        error = when {
            conflict -> null
            failure.fieldErrors.isNotEmpty() -> null
            else -> failure.message
        }
    }
}

/**
 * Impressum (#84, #147): the club's address, ZVR-Zahl, contact and bank details and "Weitere Angaben" of the public
 * site's Impressum page, in cards (design system v48, TextField "Addresses and account details"). The Obmann comes from
 * Vorstand ([onNavigate] leads there). Superusers ([editable]) change and save it; every other admin sees it read-only,
 * and the server refuses their saves anyway.
 */
@Composable
fun ImprintScreen(rpc: RpcClient, guard: UnsavedChangesGuard, editable: Boolean, onNavigate: (Route) -> Unit) {
    val service = remember(rpc) { rpc.withService<ImprintService>() }
    val scope = rememberCoroutineScope()

    var editor by remember { mutableStateOf<ImprintEditorState?>(null) }
    var loadError by remember { mutableStateOf<String?>(null) }

    /** Loads the Impressum in place of [replacing]; the answer is dropped if the editor changed meanwhile. */
    suspend fun load(replacing: ImprintEditorState? = editor) {
        val result = rpcCall { service.load() }
        if (editor !== replacing) {
            return
        }
        when (result) {
            is AdminResult.Success -> {
                editor = ImprintEditorState(result.value)
                loadError = null
            }

            // a failed reload keeps the editor and says why in it
            is AdminResult.Failure -> replacing?.let { it.error = result.error.message } ?: run { loadError = result.error.message }
        }
    }

    LaunchedEffect(service) { load() }

    val state = editor
    ReportUnsavedChanges(guard, editable && state?.hasChanges == true)

    fun save() {
        val current = editor ?: return
        if (current.busy || !current.hasChanges) {
            return
        }
        current.conflict = false
        current.error = null
        current.fieldErrors = current.draft.validate()
        if (current.fieldErrors.isNotEmpty()) {
            return
        }
        current.saving = true
        scope.launch {
            val result = rpcCall { service.save(current.loaded.versionedId, current.draft.normalized()) }
            current.saving = false
            if (editor !== current) {
                return@launch
            }
            when (result) {
                is AdminResult.Success -> editor = ImprintEditorState(result.value)
                is AdminResult.Failure -> current.showFailure(result.error)
            }
        }
    }

    fun reloadAfterConflict() {
        val current = editor ?: return
        if (current.reloading) {
            return
        }
        current.reloading = true
        scope.launch {
            try {
                load(replacing = current)
            } finally {
                current.reloading = false
            }
        }
    }

    AdminPageHeader(title = "Impressum", unsavedChanges = editable && state?.hasChanges == true) {
        PublicSiteLink("/imprint", button.block, button.modifier("link")) { Text("Auf Website ansehen ↗") }
        if (editable && state != null) {
            AdminButton(disabled = state.busy || !state.hasChanges, onClick = { editor = ImprintEditorState(state.loaded) }) {
                Text("Verwerfen")
            }
            AdminButton(
                variant = ButtonVariant.PRIMARY,
                type = ButtonType.Submit,
                disabled = state.busy || !state.hasChanges,
                form = FORM_ID,
            ) { Text(if (state.saving) "Speichert …" else "Speichern") }
        }
    }

    AdminPageBody {
        when {
            state != null -> Form(attrs = {
                id(FORM_ID)
                // one form, so it stays at the form's width (design system v27)
                classes(stack.block, stack.modifier("form"))
                attr("novalidate", "")
                onSubmit { event ->
                    event.preventDefault()
                    save()
                }
            }) {
                if (state.conflict) {
                    AdminConflictCallout(onReload = ::reloadAfterConflict)
                }
                state.error?.let {
                    P({
                        classes(alert.block)
                        attr("role", "alert")
                    }) { Text(it) }
                }

                AdminCard(title = "Verein", id = "impressum-verein") {
                    AdminCallout {
                        Text("Die Website zeigt diese Angaben auf der Seite ")
                        Strong { Text("„Impressum“") }
                        Text(", verlinkt in der Fußzeile jeder Seite. Die Adresse steht auch in der Fußzeile, Adresse und E-Mail auch auf der Seite ")
                        Strong { Text("„Verein“") }
                        Text(".")
                    }
                    if (!editable) {
                        Span({ classes(card.element("hint").className) }) { Text("Nur Superuser können das Impressum ändern.") }
                    }
                    EditorFields(state, editable) {
                        TextValue(state, editable, ImprintDraft.STREET, "impressum-strasse", "Straße")
                        Div({ classes(formGrid.block, formGrid.modifier("title-year")) }) {
                            TextValue(state, editable, ImprintDraft.POSTAL_CODE, "impressum-plz", "PLZ", inputMode = "numeric")
                            Div({ classes(formGrid.element("item").className, formGrid.element("item").modifier("span-3")) }) {
                                TextValue(state, editable, ImprintDraft.LOCALITY, "impressum-ort", "Ort")
                            }
                        }
                        TextValue(state, editable, ImprintDraft.ZVR_NUMBER, "impressum-zvr", "ZVR-Zahl")
                    }
                    ObmannValue(state.loaded.obmann, onNavigate)
                }

                AdminCard(title = "Kontakt", id = "impressum-kontakt") {
                    EditorFields(state, editable) {
                        TextValue(state, editable, ImprintDraft.CONTACT_EMAIL, "impressum-email", "E-Mail", type = InputType.Email)
                        TextValue(state, editable, ImprintDraft.CONTACT_PHONE, "impressum-telefon", "Telefon", type = InputType.Tel)
                    }
                }

                AdminCard(title = "Bankverbindung", id = "impressum-bank") {
                    EditorFields(state, editable) {
                        TextValue(state, editable, ImprintDraft.BANK_ACCOUNT_HOLDER, "impressum-kontoinhaber", "Kontoinhaber")
                        TextValue(state, editable, ImprintDraft.IBAN, "impressum-iban", "IBAN")
                        TextValue(state, editable, ImprintDraft.BIC, "impressum-bic", "BIC")
                    }
                }

                AdminCard(title = "Weitere Angaben", id = "impressum-weitere") {
                    if (editable) {
                        EditorFields(state, editable) {
                            AdminMarkdownField(
                                id = "impressum-text",
                                label = "Text",
                                value = state.draft.additionalText,
                                onValueChange = { value -> state.edit(ImprintDraft.ADDITIONAL_TEXT) { copy(additionalText = value) } },
                                error = state.errorOf(ImprintDraft.ADDITIONAL_TEXT),
                                optional = true,
                                rows = 8,
                            )
                        }
                    } else {
                        AdminMarkdownView(id = "impressum-text", label = "Text", value = state.loaded.draft.additionalText)
                    }

                    Span({ classes(card.element("hint").className) }) { Text(lastChanged(state.loaded)) }
                }
            }

            loadError != null -> P({
                classes(alert.block)
                attr("role", "alert")
            }) { Text(loadError!!) }

            else -> P { Text("Lade …") }
        }
    }
}

/** The fields of a card; nothing changes while saving or reloading, so the answer can replace the editor without dropping input. */
@Composable
private fun EditorFields(state: ImprintEditorState, editable: Boolean, content: @Composable () -> Unit) {
    if (!editable) {
        content()
        return
    }
    Fieldset({
        classes(fieldset.block)
        if (state.busy) {
            attr("disabled", "")
        }
    }) { content() }
}

/** The field [name] as an optional input for a superuser, else as a read-only value, the IBAN in groups. */
@Composable
private fun TextValue(
    state: ImprintEditorState,
    editable: Boolean,
    name: String,
    id: String,
    label: String,
    type: InputType<String> = InputType.Text,
    inputMode: String? = null,
) {
    if (!editable) {
        val value = state.loaded.draft.valueOf(name)
        AdminTextView(id = id, label = label, value = if (name == ImprintDraft.IBAN) Iban.grouped(value) else value)
        return
    }
    val tidiesOnBlur = name == ImprintDraft.IBAN || name == ImprintDraft.BIC
    AdminTextField(
        id = id,
        label = label,
        value = state.draft.valueOf(name),
        onValueChange = { value -> state.edit(name) { withValue(name, value) } },
        type = type,
        error = state.errorOf(name),
        optional = true,
        autocomplete = "off",
        inputMode = inputMode,
        onBlur = if (tidiesOnBlur) ({ state.tidy(name) }) else null,
    )
}

/**
 * The Obmann, read from Vorstand and only shown here (design system v48, TextField "Addresses and account details"):
 * a read-only value and a link to where it changes.
 */
@Composable
private fun ObmannValue(obmann: String?, onNavigate: (Route) -> Unit) {
    val labelId = "impressum-obmann-label"
    Div({ classes(field.block) }) {
        Span({
            classes(field.element("label").className)
            id(labelId)
        }) { Text("Obmann") }
        P({
            classes(field.element("value").className)
            if (obmann == null) {
                classes(field.element("value").modifier("empty"))
            }
            id("impressum-obmann")
            attr("aria-labelledby", labelId)
        }) { Text(obmann ?: "Im Vorstand hat niemand die Funktion „${Club.CONTACT_FUNCTION}“.") }
        RouteLink(Route.Board(), onNavigate, button.block, button.modifier("link")) {
            Text(if (obmann == null) "Zum Vorstand" else "Im Vorstand ändern")
        }
    }
}

private fun ImprintDraft.valueOf(name: String): String = when (name) {
    ImprintDraft.STREET -> street
    ImprintDraft.POSTAL_CODE -> postalCode
    ImprintDraft.LOCALITY -> locality
    ImprintDraft.ZVR_NUMBER -> zvrNumber
    ImprintDraft.CONTACT_EMAIL -> contactEmail
    ImprintDraft.CONTACT_PHONE -> contactPhone
    ImprintDraft.BANK_ACCOUNT_HOLDER -> bankAccountHolder
    ImprintDraft.IBAN -> iban
    ImprintDraft.BIC -> bic
    ImprintDraft.ADDITIONAL_TEXT -> additionalText
    else -> error("No Impressum field $name")
}

private fun ImprintDraft.withValue(name: String, value: String): ImprintDraft = when (name) {
    ImprintDraft.STREET -> copy(street = value)
    ImprintDraft.POSTAL_CODE -> copy(postalCode = value)
    ImprintDraft.LOCALITY -> copy(locality = value)
    ImprintDraft.ZVR_NUMBER -> copy(zvrNumber = value)
    ImprintDraft.CONTACT_EMAIL -> copy(contactEmail = value)
    ImprintDraft.CONTACT_PHONE -> copy(contactPhone = value)
    ImprintDraft.BANK_ACCOUNT_HOLDER -> copy(bankAccountHolder = value)
    ImprintDraft.IBAN -> copy(iban = value)
    ImprintDraft.BIC -> copy(bic = value)
    ImprintDraft.ADDITIONAL_TEXT -> copy(additionalText = value)
    else -> error("No Impressum field $name")
}

/** "Zuletzt geändert am 26.10.2026 von …", without the name for the migration's text or a deleted account. */
internal fun lastChanged(imprint: Imprint): String =
    "Zuletzt geändert am ${formatDate(imprint.changedAt)}" + imprint.changedBy?.let { " von $it" }.orEmpty()

private const val FORM_ID = "impressum"
